import {
  dispatchMutationLifecycleEvent,
  MutationLifecycleEvent,
  type ObjectPropertiesPatch,
  UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT,
} from '@github-ui/mutation-lifecycle-events'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  LockLockableInput,
  lockLockableMutation,
  lockLockableMutation$data,
} from './__generated__/lockLockableMutation.graphql'

type IssueMutationProps = {
  environment: Environment
  input: LockLockableInput
  onError?: (error: Error) => void
  onCompleted?: (response: lockLockableMutation$data) => void
}

/** Packages a mutation response into the `{id, values}` shape the event carries, or
 * `undefined` if the affected node is missing (e.g. an unexpectedly null payload field). */
function toObjectPropertiesPatch(
  lockableId: string,
  data: lockLockableMutation$data,
): ObjectPropertiesPatch | undefined {
  const lockedRecord = data.lockLockable?.lockedRecord
  if (!lockedRecord) return undefined
  return {id: lockableId, values: {locked: lockedRecord.locked ?? true}}
}

export function commitLockLockableMutation({
  environment,
  input: {lockableId, lockReason},
  onError,
  onCompleted,
}: IssueMutationProps) {
  const inputHash: LockLockableInput = {
    lockableId,
    lockReason,
  }

  // Constructing the optimistic event up front lets its `mutationId` be generated by the
  // `MutationLifecycleEvent` constructor, then reused for completed/error events so all
  // three correlate to this same commitLockLockableMutation call.
  const optimisticEvent = new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
    phase: 'optimistic',
    data: {id: lockableId, values: {locked: true}},
  })
  const mutationId = optimisticEvent.detail.mutationId

  const disposable = commitMutation<lockLockableMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation lockLockableMutation($input: LockLockableInput!) @raw_response_type {
        lockLockable(input: $input) {
          lockedRecord {
            ... on Issue {
              locked
            }
          }
        }
      }
    `,
    variables: {
      input: inputHash,
    },
    optimisticResponse: {
      lockLockable: {
        lockedRecord: {
          __typename: 'Issue',
          __isNode: 'Issue',
          id: lockableId,
          locked: true,
        },
      },
    },
    onError: error => {
      dispatchMutationLifecycleEvent(
        new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'error', mutationId, error}),
      )
      if (onError) onError(error)
    },
    onCompleted: response => {
      const patch = toObjectPropertiesPatch(lockableId, response)
      if (patch) {
        dispatchMutationLifecycleEvent(
          new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
            phase: 'completed',
            mutationId,
            data: patch,
          }),
        )
      }
      if (onCompleted) onCompleted(response)
    },
  })

  // Relay applies `optimisticResponse` synchronously as part of `commitMutation`,
  // so it's safe to dispatch the optimistic phase event right after committing.
  dispatchMutationLifecycleEvent(optimisticEvent)

  return disposable
}
